import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiSend } from "react-icons/fi";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInput } from "../../hooks/useSearchInput";
import { useToast } from "../../hooks/useToast";
import DateUtil from "../../util/DateUtil";

type ComponentKey =
  | "CopyFrontFields"
  | "CopyBackFields"
  | "CopySettings"
  | "CopyTheme"
  | "CopyNfc"
  | "CopyAdvancedSettings"
  | "CopyGeoLocations";

const COMPONENT_OPTIONS: Array<{
  key: ComponentKey;
  label: string;
  description?: string;
}> = [
  {
    key: "CopyFrontFields",
    label: "Fields on Front of Pass",
  },
  {
    key: "CopyBackFields",
    label: "Fields on Back of Pass",
  },
  {
    key: "CopySettings",
    label: "Settings",
    description: "Business Name, Barcode Type, Invitation SMS",
  },
  {
    key: "CopyTheme",
    label: "Theme",
    description: "Background Color, Foreground Color, Text Colour, Images",
  },
  {
    key: "CopyNfc",
    label: "NFC",
    description: "Enable NFC, NFC Data",
  },
  {
    key: "CopyAdvancedSettings",
    label: "Advanced Settings",
    description:
      "Pass Name, Two-Step Membership Application Pass, Show Member Photo, SMS Sending ID, Invitation Expiry Time Period, Pass Refresh Trigger Actions, Pass Refresh Configuration, Theme Data",
  },
  {
    key: "CopyGeoLocations",
    label: "Geo Locations",
    description: "Locations, Messages",
  },
];

const EMPTY_COMPONENTS: Record<ComponentKey, boolean> = {
  CopyFrontFields: false,
  CopyBackFields: false,
  CopySettings: false,
  CopyTheme: false,
  CopyNfc: false,
  CopyAdvancedSettings: false,
  CopyGeoLocations: false,
};

interface CopyPassConfigurationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem | null;
}

const PassConfigurationRow = ({
  passConfiguration,
  checkbox,
  bordered = true,
}: {
  passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem;
  checkbox?: React.ReactNode;
  bordered?: boolean;
}) => (
  <Flex
    align="center"
    gap={4}
    p={3}
    borderWidth={bordered ? "1px" : 0}
    borderColor="gray.200"
    borderRadius={bordered ? "md" : 0}
    bg="white"
  >
    {passConfiguration.PassPreviewImageUrl ? (
      <Image
        src={passConfiguration.PassPreviewImageUrl}
        alt={passConfiguration.DisplayName}
        maxH="72px"
        maxW="110px"
        objectFit="contain"
        borderRadius="md"
        flexShrink={0}
      />
    ) : (
      <Box w="110px" h="72px" bg="gray.100" borderRadius="md" flexShrink={0} />
    )}
    <Box flex="1" minW={0}>
      <Text fontWeight="semibold" fontSize="md" noOfLines={1}>
        {passConfiguration.DisplayName}
      </Text>
      <Text fontSize="sm" color="gray.700" noOfLines={1}>
        {passConfiguration.PassDescription}
      </Text>
      <Text fontSize="xs" color="gray.500" mt={1}>
        Created:{" "}
        {DateUtil.getLocalDateFormatFromString(
          passConfiguration.Meta?.DateCreated
        ) || "-"}
        {"  "}
        Last Updated:{" "}
        {DateUtil.getLocalDateFormatFromString(
          passConfiguration.Meta?.DateUpdated
        ) || "-"}
      </Text>
    </Box>
    {checkbox}
  </Flex>
);

export const CopyPassConfigurationDrawer = ({
  isOpen,
  onClose,
  businessId,
  passConfiguration,
}: CopyPassConfigurationDrawerProps) => {
  const apiClient = useAdminApiClient();
  const { showSuccessToast, showErrorToast } = useToast();
  const [selectedTargetIds, setSelectedTargetIds] = useState<Set<string>>(
    new Set()
  );
  const [components, setComponents] =
    useState<Record<ComponentKey, boolean>>(EMPTY_COMPONENTS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    query,
    debouncedQuery,
    element: searchInputElement,
    setQuery,
  } = useSearchInput({
    placeholder: "Search pass configurations",
  });

  const sourceId = passConfiguration?.Meta?.Id;

  const { items: targetPassConfigurations, isLoading: isLoadingTargets } =
    usePaginatedAdminApiRequest(
      (client) =>
        client.getBusinessDigitalMembershipCardPassConfiguration(
          businessId,
          debouncedQuery
        ),
      (client, _, continuationToken) =>
        client.getBusinessDigitalMembershipCardPassConfiguration(
          businessId,
          debouncedQuery,
          undefined,
          continuationToken
        ),
      [businessId, debouncedQuery, isOpen]
    );

  const targetOptions = useMemo(
    () =>
      (targetPassConfigurations ?? []).filter(
        (item) => item.Meta?.Id && item.Meta.Id !== sourceId
      ),
    [targetPassConfigurations, sourceId]
  );

  const allComponentsSelected = COMPONENT_OPTIONS.every(
    (option) => components[option.key]
  );
  const anyComponentSelected = COMPONENT_OPTIONS.some(
    (option) => components[option.key]
  );
  const canSubmit =
    !!sourceId && selectedTargetIds.size > 0 && anyComponentSelected;

  useEffect(() => {
    if (!isOpen) {
      setSelectedTargetIds(new Set());
      setComponents(EMPTY_COMPONENTS);
      setIsSubmitting(false);
      setQuery("");
    }
  }, [isOpen, setQuery]);

  const toggleTarget = (passConfigurationId: string) => {
    setSelectedTargetIds((prev) => {
      const next = new Set(prev);
      if (next.has(passConfigurationId)) {
        next.delete(passConfigurationId);
      } else {
        next.add(passConfigurationId);
      }
      return next;
    });
  };

  const toggleSelectAllComponents = (checked: boolean) => {
    setComponents(
      COMPONENT_OPTIONS.reduce(
        (acc, option) => {
          acc[option.key] = checked;
          return acc;
        },
        { ...EMPTY_COMPONENTS }
      )
    );
  };

  const toggleComponent = (key: ComponentKey, checked: boolean) => {
    setComponents((prev) => ({ ...prev, [key]: checked }));
  };

  const onSubmit = async () => {
    if (!sourceId || !canSubmit) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await apiClient.copyPassConfiguration(businessId, {
        SourcePassConfigurationId: sourceId,
        TargetPassConfigurationIds: Array.from(selectedTargetIds),
        CopyFrontFields: components.CopyFrontFields,
        CopyBackFields: components.CopyBackFields,
        CopySettings: components.CopySettings,
        CopyTheme: components.CopyTheme,
        CopyNfc: components.CopyNfc,
        CopyAdvancedSettings: components.CopyAdvancedSettings,
        CopyGeoLocations: components.CopyGeoLocations,
      });

      if (!result.ok) {
        showErrorToast(result.message || "Failed to copy pass configuration.");
        return;
      }

      const succeededCount =
        result.data.SucceededPassConfigurationIds?.length ?? 0;
      const errors = result.data.Errors ?? [];

      if (errors.length === 0) {
        showSuccessToast(
          `Configuration copied to ${succeededCount} pass${
            succeededCount === 1 ? "" : "es"
          }.`
        );
        onClose();
        return;
      }

      if (succeededCount > 0) {
        showErrorToast(
          `Copied to ${succeededCount} pass${
            succeededCount === 1 ? "" : "es"
          }, but ${errors.length} failed. ${errors
            .map((error) => error.ErrorMessage)
            .join(" ")}`
        );
        return;
      }

      showErrorToast(
        errors.map((error) => error.ErrorMessage).join(" ") ||
          "Failed to copy pass configuration."
      );
    } catch (error) {
      console.error("Copy pass configuration failed:", error);
      showErrorToast("An error occurred while copying pass configuration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !passConfiguration) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size="lg"
      preserveScrollBarGap
    >
      <DrawerOverlay />
      <DrawerContent maxWidth="720px" width="100%">
        <DrawerCloseButton />
        <DrawerHeader>COPY CONFIGURATION TO OTHER PASSES</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={6}>
            <Box>
              <Text fontWeight="semibold" fontSize="lg" mb={2}>
                Source Pass
              </Text>
              <PassConfigurationRow passConfiguration={passConfiguration} />
            </Box>

            <Box>
              <Text fontWeight="semibold" fontSize="lg" mb={1}>
                Select Target Passes
              </Text>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Select one or more target passes below to receive the
                configuration.
              </Text>
              <Box mb={3}>{searchInputElement}</Box>

              {isLoadingTargets && (
                <Flex justify="center" py={6}>
                  <Spinner />
                </Flex>
              )}

              {!isLoadingTargets && targetOptions.length === 0 && (
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                  color="gray.600"
                >
                  {query
                    ? `No pass configurations found matching '${query}'.`
                    : "No other pass configurations available."}
                </Box>
              )}

              {!isLoadingTargets && targetOptions.length > 0 && (
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                >
                  {targetOptions.map((target, index) => {
                    const targetId = target.Meta?.Id!;
                    return (
                      <Box
                        key={targetId}
                        borderTopWidth={index === 0 ? 0 : "1px"}
                        borderColor="gray.200"
                      >
                        <PassConfigurationRow
                          passConfiguration={target}
                          bordered={false}
                          checkbox={
                            <Checkbox
                              size="lg"
                              isChecked={selectedTargetIds.has(targetId)}
                              onChange={() => toggleTarget(targetId)}
                              aria-label={`Select ${target.DisplayName}`}
                              mx={2}
                            />
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Box>
              <Text fontWeight="semibold" fontSize="lg" mb={1}>
                Select Pass Components to Copy
              </Text>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Select whether to copy all settings or only specific components.
              </Text>
              <VStack align="stretch" spacing={3}>
                <Checkbox
                  size="lg"
                  isChecked={allComponentsSelected}
                  isIndeterminate={
                    anyComponentSelected && !allComponentsSelected
                  }
                  onChange={(e) => toggleSelectAllComponents(e.target.checked)}
                  alignItems="flex-start"
                >
                  <Text fontSize="md" lineHeight="1.25">
                    Select All (Entire Configuration)
                  </Text>
                </Checkbox>
                {COMPONENT_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.key}
                    size="lg"
                    isChecked={components[option.key]}
                    onChange={(e) =>
                      toggleComponent(option.key, e.target.checked)
                    }
                    alignItems="flex-start"
                  >
                    <Box>
                      <Text fontSize="md" lineHeight="1.25">
                        {option.label}
                      </Text>
                      {option.description && (
                        <Text fontSize="sm" color="gray.500" lineHeight="1.3">
                          {option.description}
                        </Text>
                      )}
                    </Box>
                  </Checkbox>
                ))}
              </VStack>
            </Box>
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <Button
            colorScheme="cherryButton"
            leftIcon={<FiSend />}
            onClick={onSubmit}
            isLoading={isSubmitting}
            isDisabled={!canSubmit || isSubmitting}
          >
            Copy Configuration
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
